import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

// Extra files attached manually to an email — each file is uploaded right away.
export default function ExtraAttachmentsPicker({ uploadUrl, value, onChange, disabled = false, testid = "extra-attachments" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const addFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading((n) => n + list.length);
    const added = [];
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data);
      } catch (e) {
        toast.error(`${f.name} : ${e?.response?.data?.detail || "envoi impossible"}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (added.length) onChange([...(value || []), ...added]);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="border border-zinc-200 rounded-md p-3 space-y-2 bg-white/60" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-zinc-800 inline-flex items-center gap-1.5">
          <Paperclip size={13} /> Pièces jointes supplémentaires
        </span>
        <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} data-testid={`${testid}-add`}
          className="inline-flex items-center gap-1 px-2.5 h-7 text-xs rounded-md border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50 transition-colors">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Ajouter des fichiers
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" data-testid={`${testid}-input`}
          onChange={(e) => addFiles(e.target.files)} />
      </div>
      {(value || []).length === 0 && !uploading && (
        <p className="text-[11px] text-zinc-500">Aucun fichier ajouté (20 Mo max par fichier).</p>
      )}
      {(value || []).map((a) => (
        <div key={a.attachment_id} className="flex items-center gap-2 text-xs" data-testid={`${testid}-item-${a.attachment_id}`}>
          <Paperclip size={12} className="text-zinc-400 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{a.filename}</span>
          <span className="font-mono text-[10px] text-zinc-500">{fmtSize(a.size || 0)}</span>
          <button type="button" disabled={disabled} onClick={() => onChange(value.filter((x) => x.attachment_id !== a.attachment_id))}
            title="Retirer" data-testid={`${testid}-remove-${a.attachment_id}`} className="p-1 rounded hover:bg-red-50 text-red-600">
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}
